import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";

export default class extends Controller {
  static values = {
    warningTime: { type: Number, default: 1000 * 60 * 115 }, // 1 hour 55 minutes
    logoutTime: { type: Number, default: 1000 * 60 * 120 }, // 2 hours
    whiteLabeled: { type: Boolean, default: false }
  };

  static targets = ["warningModal", "countdown"];

  // Define activity events as a class property
  activityEvents = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart"
  ];

  // Define debounced function as a class property
  debouncedUpdateLastActivity = debounce((event) => {
    // Don't update activity if modal is showing
    if (this.isModalShowing) {
      return;
    }
    this.updateLastActivity();
  }, 250);

  connect() {
    // Set initial activity timestamp
    this.updateLastActivity();
    // Setup activity events
    this.setupActivityTracking();
    // Setup modal events
    this.setupModalEvents();
    // Start inactivity checking
    this.startInactivityCheck();
  }

  disconnect() {
    this.cleanupActivityTracking();
    this.cleanupModalEvents();
    this.stopInactivityCheck();
    this.stopCountdown();
    this.hideWarning();
  }

  setupModalEvents() {
    $(this.warningModalTarget).on("show.bs.modal", () => {
      this.isModalShowing = true;
      this.startCountdown();
    });

    $(this.warningModalTarget).on("hidden.bs.modal", () => {
      this.isModalShowing = false;
      this.updateLastActivity();
      this.stopCountdown();
    });
  }

  cleanupModalEvents() {
    if (this.hasWarningModalTarget) {
      $(this.warningModalTarget).off("show.bs.modal hidden.bs.modal");
    }
  }

  formatTimeLeft(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes} minute(s) ${remainingSeconds} second(s)`;
    }
    return `${remainingSeconds} second(s)`;
  }

  startCountdown() {
    const timeLeft = Math.ceil(
      (this.logoutTimeValue - this.warningTimeValue) / 1000
    );
    this.updateCountdown(timeLeft);

    this.countdownInterval = setInterval(() => {
      const newTimeLeft = Math.ceil(
        (this.logoutTimeValue - (Date.now() - this.getLastActivity())) / 1000
      );

      if (newTimeLeft <= 0) {
        this.stopCountdown();
        return;
      }

      this.updateCountdown(newTimeLeft);
    }, 1000);
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  updateCountdown(seconds) {
    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = this.formatTimeLeft(seconds);
    }
  }

  setupActivityTracking() {
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.debouncedUpdateLastActivity);
    });
  }

  cleanupActivityTracking() {
    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.debouncedUpdateLastActivity);
    });
  }

  startInactivityCheck() {
    this.checkInactivity();
    this.inactivityInterval = setInterval(() => this.checkInactivity(), 1000);
  }

  stopInactivityCheck() {
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
      this.inactivityInterval = null;
    }
  }

  staySignedIn() {
    this.updateLastActivity();
    this.hideWarning();
  }

  updateLastActivity() {
    localStorage.setItem("lastActivity", Date.now().toString());
  }

  getLastActivity() {
    return parseInt(
      localStorage.getItem("lastActivity") || Date.now().toString()
    );
  }

  hideWarning() {
    if (this.hasWarningModalTarget) {
      $(this.warningModalTarget).modal("hide");
    }
  }

  showWarning() {
    // Only show the warning modal if it's not already open
    if (this.hasWarningModalTarget && !this.isModalShowing) {
      $(this.warningModalTarget).modal("show");
    }
  }

  checkInactivity() {
    const now = Date.now();
    const lastActivity = this.getLastActivity();
    const inactiveTime = now - lastActivity;

    if (inactiveTime >= this.logoutTimeValue) {
      window.location.href = `/user/sign_out?inactivity=true&white_labeled=${this.whiteLabeledValue}`;
    } else if (inactiveTime >= this.warningTimeValue) {
      this.showWarning();
    }
  }
}
